<template>
  <q-select
    outlined
    color="teal"
    v-model="model"
    use-input
    input-debounce="0"
    :options="filteredOptions"
    @filter="onFilter"
    :loading="loading"
    :hint="hint"
    :disable="disable"
    :readonly="readonly"
    :clearable="clearable"
    :placeholder="placeholder"
    :label="label"
    option-value="value"
    option-label="label"
    emit-value
    map-options
    v-bind="$attrs"
  >
    <template v-slot:no-option>
      <q-item>
        <q-item-section class="text-grey">
          {{ noOptionMessage }}
        </q-item-section>
      </q-item>
    </template>

    <template v-for="(_, slot) in $slots" v-slot:[slot]="scope">
      <slot :name="slot" v-bind="scope || {}" />
    </template>
  </q-select>
</template>

<script>
import { repository } from 'src/jtech'
import debounce from 'lodash/debounce'
import { JtSelect } from 'jtech-webcomps'

export default {
  name: 'AutocompleteSelect',
  mixins: [JtSelect],

  props: {
    modelValue: null,
    nmInterface: { type: String, required: true },
    nmAtributo: { type: String, required: true },
    nmAtributoName: { type: String, required: true },
    hint: { type: String, default: '' },
    placeholder: { type: String, default: '' },
    label: { type: String, default: '' },
    disable: Boolean,
    readonly: Boolean,
    clearable: Boolean,
    minCharacters: { type: Number, default: 3 },
    inputDebounce: { type: Number, default: 500 },
    noResultsText: { type: String, default: '' },
    preLoadedAutocompleteOptions: {
      type: Array,
      default: () => []
    }
  },

  emits: ['update:modelValue', 'loading-change', 'error', 'input-value'],

  data () {
    return {
      model: this.modelValue,
      loading: false,
      allOptions: [],
      filteredOptions: [],
      searchTerm: '',
      fetchOptions: null
    }
  },

  watch: {
    model (newVal) {
      this.__set(newVal)
      this.saveIntoPreLoadOptions(newVal)

      this.$emit('update:modelValue', newVal)
    },
    modelValue (newVal) {
      this.model = newVal
    },
    preLoadedAutocompleteOptions: {
      handler (newVal, oldVal) {
        const currentPreload = newVal.find(opt => opt.nmAtributo === this.nmAtributo)
        const oldPreload = oldVal?.find(opt => opt.nmAtributo === this.nmAtributo)

        if (oldPreload?.options?.length > 0 && (!currentPreload?.options || currentPreload.options.length === 0)) {
          this.model = null
          this.filteredOptions = []
          this.allOptions = []
          this.searchTerm = ''
          this.$emit('update:modelValue', null)
        } else {
          this.initFromPreLoadedOptions()
        }
      },
      deep: true,
      immediate: false
    }
  },

  computed: {
    noOptionMessage () {
      if (this.loading) return this.$gettext('LOADING')
      if (this.searchTerm.length < this.minCharacters) {
        return this.$gettextInterpolate(
          this.$gettext('MINIMUM_CHARACTERES'),
          { min: this.minCharacters }
        )
      }
      return this.noResultsText
    }
  },

  mounted () {
    this.fetchOptions = debounce(this.loadFromAPI, this.inputDebounce)
    this.initFromPreLoadedOptions()
  },

  methods: {
    onFilter (val, update) {
      this.searchTerm = val || ''
      this.$emit('input-value', this.searchTerm)

      if (!val || val.length < this.minCharacters) {
        update(() => (this.filteredOptions = []))
        return
      }

      const local = this.allOptions.filter(opt =>
        opt.label.toLowerCase().includes(val.toLowerCase())
      )

      update(() => (this.filteredOptions = local))
      this.fetchOptions(val, update)
    },

    async loadFromAPI (term, update) {
      if (!term || term.length < this.minCharacters) return

      this.loading = true
      this.$emit('loading-change', true)

      try {
        const response = await repository.resolveSelectAjax(this.nmInterface, {
          nmAtributo: this.nmAtributoName,
          [this.nmAtributoName]: term
        })

        const newOptions = response?.reRender?.[this.nmAtributo]?.map(({ label, value }) => ({ label, value })) || []

        const uniqueOptions = newOptions.filter(
          opt => !this.allOptions.find(existing => existing.value === opt.value)
        )

        this.allOptions.push(...uniqueOptions)

        const filtered = this.allOptions.filter(opt =>
          opt.label.toLowerCase().includes(term.toLowerCase())
        )

        update(() => {
          this.filteredOptions = filtered
        })

        if (filtered.length === 1) {
          this.model = filtered[0].value
        }
      } catch (e) {
        console.error(e)
        this.$emit('error', e)
      } finally {
        this.loading = false
        this.$emit('loading-change', false)
      }
    },

    initFromPreLoadedOptions () {
      const found = this.preLoadedAutocompleteOptions.find(
        opt => opt.nmAtributo === this.nmAtributo
      )

      if (found && found.options?.length) {
        this.model = found.options[0].value
        this.filteredOptions = found.options
      } else {
        this.model = null
        this.filteredOptions = []
      }
    },

    saveIntoPreLoadOptions (newVal) {
      const selectedOption = this.filteredOptions.find(opt => opt.value === newVal)
      this.preLoadedAutocompleteOptions.forEach(preLoad => {
        if (preLoad.nmAtributo === this.nmAtributo && preLoad.options) {
          preLoad.options = [selectedOption]
        }
      })
    }

  }
}
</script>
